<template>
  <div class="tender-entity-h5">
    <!-- 搜索框 -->
    <div class="search-box-h5">
      <el-input 
        v-model="searchKeyword" 
        placeholder="请输入产品名称、物料号、型号搜索" 
        clearable
        @clear="handleSearch"
        @keyup.enter="handleSearch"
        class="search-input-h5">
        <template #prefix>
          <el-icon>
            <Search />
          </el-icon>
        </template>
      </el-input>
      <el-button type="primary" @click="handleSearch" class="search-btn-h5">
        搜索
      </el-button>
    </div>

    <!-- 网格视图 -->
    <div class="grid-view-container-h5" v-loading="loading">
      <div v-if="tableData.length > 0" class="grid-list-h5">
        <div 
          v-for="(row, index) in tableData" 
          :key="index" 
          class="grid-item-h5">
          <!-- 合同名称 -->
          <div class="grid-header-h5">
            <h4 class="grid-title-h5">{{ getContractName(row) }}</h4>
            <el-tag
              v-if="getContractDisplayTag(row)"
              :type="getContractDisplayTag(row).type"
              size="small"
              effect="light"
              class="display-tag">
              {{ getContractDisplayTag(row).text }}
            </el-tag>
          </div>

          <!-- 基本信息 -->
          <div class="grid-body-h5">
            <div class="grid-info-h5" v-if="getContractWeightDisplay(row)">
              <el-icon class="grid-icon-h5">
                <Shop />
              </el-icon>
              <span>{{ isContractQuery ? '总重量(KG)' : '重量' }}: {{ getContractWeightDisplay(row) }}</span>
            </div>
            
            <div class="grid-info-h5" v-if="getContractPriceDisplay(row)">
              <el-icon class="grid-icon-h5">
                <Coin />
              </el-icon>
              <span>{{ isContractQuery ? '总价格' : '价格' }}: {{ getContractPriceDisplay(row) }}</span>
            </div>
           
            <div class="grid-info-h5" v-if="row.brand">
              <el-icon class="grid-icon-h5">
                <Box />
              </el-icon>
              <span>{{ row.brand }}</span>
            </div>
            
            <div class="grid-info-h5" v-if="row.model">
              <el-icon class="grid-icon-h5">
                <Setting />
              </el-icon>
              <span>{{ row.model }}</span>
            </div>
            
            <div class="grid-info-h5" v-if="row.materialNumber">
              <el-icon class="grid-icon-h5">
                <Document />
              </el-icon>
              <span>物料号: {{ row.materialNumber }}</span>
            </div>
            
            <div class="grid-info-h5" v-if="row.manufacturer">
              <el-icon class="grid-icon-h5">
                <Shop />
              </el-icon>
              <span>{{ row.manufacturer }}</span>
            </div>

            <!-- 材料列表展开 -->
            <div 
              v-if="(isContractQuery && getContractDetailList(row).length > 0) || (!isContractQuery && row.list && row.list.length > 0)" 
              class="grid-material-section-h5">
              <el-collapse>
                <!-- 材料列表 (virtual == 0) -->
                <el-collapse-item 
                  v-if="(isContractQuery ? (getContractMaterialList(row).length > 0 || getContractPlainItems(row).length > 0) : (getMaterialList(row).length > 0 || getPlainItems(row.list).length > 0))" 
                  :title="`材料列表 (${isContractQuery ? (getContractMaterialList(row).length + getContractPlainItems(row).length) : (getMaterialList(row).length + getPlainItems(row.list).length)} 项)`" 
                  :name="`grid-material-${index}`">
                  <div class="grid-material-table-wrapper-h5">
                    <el-table
                      :data="isContractQuery ? getContractMaterialList(row) : getMaterialList(row)"
                      size="small"
                      border
                      stripe
                      :show-header="true"
                      style="width: 100%"
                      :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: '600', textAlign: 'center', fontSize: '12px' }"
                      :cell-style="{ textAlign: 'center', padding: '8px 4px', fontSize: '12px' }">
                      <el-table-column prop="nameElement" label="材料名" min-width="100" align="center" show-overflow-tooltip>
                        <template #default="{ row: item }">
                          {{ item.nameElement || '--' }}
                        </template>
                      </el-table-column>
                      <el-table-column prop="heaveyAll" label="总重量(KG)" min-width="100" align="center">
                        <template #default="{ row: item }">
                          {{ item.heaveyAll != null ? formatFieldValue('heaveyAll', item.heaveyAll) : (item.quantityAll ?? item.quantity ?? '--') }}
                        </template>
                      </el-table-column>
                      <el-table-column prop="moneyALL" label="总价格" min-width="100" align="center">
                        <template #default="{ row: item }">
                          {{ item.moneyALL != null ? formatFieldValue('moneyALL', item.moneyALL) : '--' }}
                        </template>
                      </el-table-column>
                    </el-table>
                    <div 
                      v-if="(isContractQuery ? getContractPlainItems(row).length > 0 : getPlainItems(row.list).length > 0)" 
                      class="grid-material-plain-items-h5">
                      <div 
                        v-for="(item, itemIdx) in (isContractQuery ? getContractPlainItems(row) : getPlainItems(row.list))" 
                        :key="itemIdx" 
                        class="grid-material-text-h5">
                        {{ item }}
                      </div>
                    </div>
                  </div>
                </el-collapse-item>
                
                <!-- 虚拟列表 (virtual == 1) -->
                <el-collapse-item 
                  v-if="(isContractQuery ? getContractVirtualList(row).length > 0 : getVirtualList(row).length > 0)" 
                  :title="`虚拟列表 (${isContractQuery ? getContractVirtualList(row).length : getVirtualList(row).length} 项)`" 
                  :name="`grid-virtual-${index}`">
                  <div class="grid-material-table-wrapper-h5">
                    <el-table
                      :data="isContractQuery ? getContractVirtualList(row) : getVirtualList(row)"
                      size="small"
                      border
                      stripe
                      :show-header="true"
                      style="width: 100%"
                      :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: '600', textAlign: 'center', fontSize: '12px' }"
                      :cell-style="{ textAlign: 'center', padding: '8px 4px', fontSize: '12px' }">
                      <el-table-column prop="nameElement" label="材料名" min-width="100" align="center" show-overflow-tooltip>
                        <template #default="{ row: item }">
                          {{ item.nameElement || '--' }}
                        </template>
                      </el-table-column>
                      <el-table-column prop="heaveyAll" label="总重量(KG)" min-width="100" align="center">
                        <template #default="{ row: item }">
                          {{ item.heaveyAll != null ? formatFieldValue('heaveyAll', item.heaveyAll) : (item.quantityAll ?? item.quantity ?? '--') }}
                        </template>
                      </el-table-column>
                      <el-table-column prop="moneyALL" label="总价格" min-width="100" align="center">
                        <template #default="{ row: item }">
                          {{ item.moneyALL != null ? formatFieldValue('moneyALL', item.moneyALL) : '--' }}
                        </template>
                      </el-table-column>
                      <el-table-column label="图片" min-width="100" align="center">
                        <template #default="{ row: item }">
                          <el-button 
                            type="primary" 
                            size="small"
                            :disabled="!getItemImageList(item) || getItemImageList(item).length === 0"
                            @click="showImageList(getItemImageList(item))">
                            查看图片 ({{ getItemImageList(item) ? getItemImageList(item).length : 0 }})
                          </el-button>
                        </template>
                      </el-table-column>
                    </el-table>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </div>
            
            <!-- 合同查询模式下的详情条例展开 -->
            <div 
              v-if="isContractQuery && getContractDetailList(row).length > 0" 
              class="grid-detail-section-h5">
              <el-collapse>
                <el-collapse-item 
                  :title="`详情条例 (${getContractDetailList(row).length} 项)`" 
                  :name="`detail-${index}`">
                  <div 
                    v-for="(detail, detailIdx) in getContractDetailList(row)" 
                    :key="detailIdx" 
                    class="grid-detail-item-h5">
                    <div class="grid-detail-header-h5">
                      <span class="grid-detail-index-h5">{{ detailIdx + 1 }}.</span>
                      <div class="grid-detail-info-h5">
                        <div class="grid-detail-row-h5" v-if="detail.brand">
                          <span class="grid-detail-label-h5">产品:</span>
                          <span class="grid-detail-value-h5">{{ formatDetailField(detail.brand) }}</span>
                        </div>
                        <div class="grid-detail-row-h5" v-if="detail.model">
                          <span class="grid-detail-label-h5">型号:</span>
                          <span class="grid-detail-value-h5">{{ formatDetailField(detail.model) }}</span>
                        </div>
                        <div class="grid-detail-row-h5" v-if="detail.weight">
                          <span class="grid-detail-label-h5">重量:</span>
                          <span class="grid-detail-value-h5">{{ formatDetailWeight(detail.weight) }}</span>
                        </div>
                        <div class="grid-detail-row-h5" v-if="detail.materialNumber">
                          <span class="grid-detail-label-h5">物料号:</span>
                          <span class="grid-detail-value-h5">{{ formatDetailField(detail.materialNumber) }}</span>
                        </div>
                        <div class="grid-detail-row-h5" v-if="detail.manufacturer">
                          <span class="grid-detail-label-h5">制造商:</span>
                          <span class="grid-detail-value-h5">{{ formatDetailField(detail.manufacturer) }}</span>
                        </div>
                      </div>
                    </div>
                    
                    <!-- 材料明细 -->
                    <div 
                      v-if="getDetailMaterialList(detail).length > 0 || getDetailVirtualList(detail).length > 0 || getDetailPlainItems(detail).length > 0" 
                      class="grid-detail-materials-h5">
                      <!-- 材料列表 (virtual == 0) -->
                      <div 
                        v-if="getDetailMaterialList(detail).length > 0 || getDetailPlainItems(detail).length > 0" 
                        class="grid-detail-material-group-h5">
                        <div class="grid-detail-material-group-title-h5">
                          材料列表 ({{ getDetailMaterialList(detail).length + getDetailPlainItems(detail).length }} 项)
                        </div>
                        <div class="grid-material-table-wrapper-h5">
                          <el-table
                            :data="getDetailMaterialList(detail)"
                            size="small"
                            border
                            stripe
                            :show-header="true"
                            style="width: 100%"
                            :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: '600', textAlign: 'center', fontSize: '12px' }"
                            :cell-style="{ textAlign: 'center', padding: '8px 4px', fontSize: '12px' }">
                            <el-table-column prop="nameElement" label="材料名" min-width="100" align="center" show-overflow-tooltip>
                              <template #default="{ row: item }">
                                {{ item.nameElement || '--' }}
                              </template>
                            </el-table-column>
                            <el-table-column prop="heaveyAll" label="总重量(KG)" min-width="100" align="center">
                              <template #default="{ row: item }">
                                {{ item.heaveyAll != null ? formatFieldValue('heaveyAll', item.heaveyAll) : (item.quantityAll ?? item.quantity ?? '--') }}
                              </template>
                            </el-table-column>
                            <el-table-column prop="moneyALL" label="总价格" min-width="100" align="center">
                              <template #default="{ row: item }">
                                {{ item.moneyALL != null ? formatFieldValue('moneyALL', item.moneyALL) : '--' }}
                              </template>
                            </el-table-column>
                          </el-table>
                          <div 
                            v-if="getDetailPlainItems(detail).length > 0" 
                            class="grid-material-plain-items-h5">
                            <div 
                              v-for="(item, itemIdx) in getDetailPlainItems(detail)" 
                              :key="itemIdx" 
                              class="grid-material-text-h5">
                              {{ item }}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- 虚拟列表 (virtual == 1) -->
                      <div 
                        v-if="getDetailVirtualList(detail).length > 0" 
                        class="grid-detail-material-group-h5">
                        <div class="grid-detail-material-group-title-h5">
                          虚拟列表 ({{ getDetailVirtualList(detail).length }} 项)
                        </div>
                        <div class="grid-material-table-wrapper-h5">
                          <el-table
                            :data="getDetailVirtualList(detail)"
                            size="small"
                            border
                            stripe
                            :show-header="true"
                            style="width: 100%"
                            :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: '600', textAlign: 'center', fontSize: '12px' }"
                            :cell-style="{ textAlign: 'center', padding: '8px 4px', fontSize: '12px' }">
                            <el-table-column prop="nameElement" label="材料名" min-width="100" align="center" show-overflow-tooltip>
                              <template #default="{ row: item }">
                                {{ item.nameElement || '--' }}
                              </template>
                            </el-table-column>
                            <el-table-column prop="heaveyAll" label="总重量(KG)" min-width="100" align="center">
                              <template #default="{ row: item }">
                                {{ item.heaveyAll != null ? formatFieldValue('heaveyAll', item.heaveyAll) : (item.quantityAll ?? item.quantity ?? '--') }}
                              </template>
                            </el-table-column>
                            <el-table-column prop="moneyALL" label="总价格" min-width="100" align="center">
                              <template #default="{ row: item }">
                                {{ item.moneyALL != null ? formatFieldValue('moneyALL', item.moneyALL) : '--' }}
                              </template>
                            </el-table-column>
                            <el-table-column label="图片" min-width="100" align="center">
                              <template #default="{ row: item }">
                                <el-button 
                                  type="primary" 
                                  size="small"
                                  :disabled="!getItemImageList(item) || getItemImageList(item).length === 0"
                                  @click="showImageList(getItemImageList(item))">
                                  查看图片 ({{ getItemImageList(item) ? getItemImageList(item).length : 0 }})
                                </el-button>
                              </template>
                            </el-table-column>
                          </el-table>
                        </div>
                      </div>
                    </div>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </div>
          </div>
          
          <!-- 底部图片按钮 -->
          <div class="grid-footer-h5">
            <el-button 
              type="primary" 
              size="default"
              :disabled="!row.imgListbyidlist || row.imgListbyidlist.length === 0" 
              @click="showImageList(row)"
              class="image-btn-h5-full">
              <el-icon>
                <Picture />
              </el-icon>
              图片 ({{ row.imgListbyidlist ? row.imgListbyidlist.length : 0 }})
            </el-button>
          </div>
        </div>
      </div>
      
      <div v-else class="empty-grid-h5">
        <el-empty description="暂无数据" />
      </div>
    </div>

    <!-- 图片展示弹窗 -->
    <el-dialog 
      v-model="imageDialogVisible" 
      title="图片列表" 
      width="90%" 
      :before-close="handleCloseImageDialog"
      class="image-dialog-h5">
      <div v-if="currentImageList && currentImageList.length > 0" class="image-list-container-h5">
        <div 
          v-for="(img, index) in currentImageList" 
          :key="index" 
          class="image-item-h5">
          <el-image 
            :src="img" 
            :preview-src-list="previewImageList" 
            :initial-index="index" 
            fit="cover" 
            :lazy="true"
            class="image-preview-h5" 
            :preview-teleported="true">
            <template #error>
              <div class="image-slot-h5">
                <el-icon>
                  <Picture />
                </el-icon>
                <div>加载失败</div>
              </div>
            </template>
          </el-image>
        </div>
      </div>
      <div v-else class="empty-image-h5">
        <el-empty description="暂无图片" />
      </div>
    </el-dialog>

    <!-- 分页 -->
    <div class="pagination-box-h5">
      <el-pagination 
        v-model:current-page="page" 
        v-model:page-size="pageSize" 
        :page-sizes="[10, 20, 50]" 
        :total="total" 
        layout="prev, pager, next, total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange" 
        small />
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';

import request from '@/api/api';
import {
  Box,
  Coin,
  Document,
  Picture,
  Search,
  Setting,
  Shop,
} from '@element-plus/icons-vue';

export default {
  name: 'TenderEntityH5',
  components: {
    Box,
    Coin,
    Document,
    Picture,
    Search,
    Setting,
    Shop,
  },
  data() {
    return {
      searchKeyword: '',
      tableData: [],
      loading: false,
      page: 1,
      pageSize: 10,
      total: 0,
      imageDialogVisible: false,
      currentImageList: [],
      selectedTenderList: []
    };
  },
  computed: {
    previewImageList() {
      return this.currentImageList || [];
    },
    selectedTenderIds() {
      if (!Array.isArray(this.selectedTenderList)) {
        return [];
      }
      return this.selectedTenderList
        .map(item => {
          if (typeof item === 'object' && item !== null) {
            return item.id ?? item.value ?? item.tenderId ?? item.tenderID ?? item.ID;
          }
          return item;
        })
        .filter(id => id !== undefined && id !== null && id !== '');
    },
    isContractQuery() {
      return this.selectedTenderIds.length > 0;
    }
  },
  mounted() {
    this.refreshData();
    // 确保 DOM 更新完成后再进行其他操作
    this.$nextTick(() => {
      // 可以在这里添加初始化后的操作
    });
  },
  watch: {
    page(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.refreshData();
      }
    },
    pageSize(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.page = 1;
        this.refreshData();
      }
    }
  },
  methods: {
    getContractName(row) {
      if (!row) return '未知';
      return (
        row.tenderName ||
        row.name ||
        row.contractName ||
        row.title ||
        row.tenderBatchName ||
        '未知'
      );
    },
    getContractDisplayTag(row) {
      const val = row?.display ?? row?.visible ?? row?.isDisplay;
      if (val === 1 || val === '1' || val === true) {
        return { text: '可见', type: 'success' };
      }
      if (val === 0 || val === '0' || val === false) {
        return { text: '不可见', type: 'info' };
      }
      return null;
    },
    getFirstValidValue(values) {
      if (!Array.isArray(values)) return undefined;
      return values.find(value => value !== undefined && value !== null && value !== '');
    },
    getContractPriceValue(row) {
      if (!row) return undefined;
      return this.getFirstValidValue([
        row.priceAll,
        row.moneyAll,
        row.totalPrice,
        row.totalMoney,
        row.price
      ]);
    },
    getContractWeightValue(row) {
      if (!row) return undefined;
      return this.getFirstValidValue([row.heaveyAll, row.totalWeight, row.weight]);
    },
    formatContractPrice(value) {
      if (value === undefined || value === null || value === '') return '';
      return this.formatFieldValue('moneyALL', value);
    },
    formatContractWeight(value) {
      if (value === undefined || value === null || value === '') return '';
      return `${this.formatFieldValue('heaveyAll', value)} KG`;
    },
    getContractPriceDisplay(row) {
      const value = this.getContractPriceValue(row);
      if (value === undefined || value === null || value === '') {
        return '';
      }
      return this.formatContractPrice(value);
    },
    getContractWeightDisplay(row) {
      const value = this.getContractWeightValue(row);
      if (value === undefined || value === null || value === '') {
        return '';
      }
      return this.formatContractWeight(value);
    },
    getContractDetailList(row) {
      if (!row) return [];
      const list = row.bidBrandNameVoList || row.bidBrandNameList || [];
      return Array.isArray(list) ? list : [];
    },
    getDetailMaterialList(detail) {
      if (!detail) return [];
      const list = detail.list || detail.materialList || [];
      if (!Array.isArray(list)) return [];
      return list.filter(item => {
        if (typeof item !== 'object' || item === null) return false;
        const virtual = item.virtual;
        return virtual === 0 || virtual === '0' || virtual === undefined || virtual === null;
      });
    },
    getDetailVirtualList(detail) {
      if (!detail) return [];
      const list = detail.list || detail.materialList || [];
      if (!Array.isArray(list)) return [];
      return list.filter(item => {
        if (typeof item !== 'object' || item === null) return false;
        return item.virtual === 1 || item.virtual === '1';
      });
    },
    getDetailPlainItems(detail) {
      if (!detail) return [];
      const list = detail.list || detail.materialList || [];
      if (!Array.isArray(list)) return [];
      return list.filter(item => typeof item !== 'object' || item === null);
    },
    getMaterialList(row) {
      if (!row || !row.list || !Array.isArray(row.list)) return [];
      return row.list.filter(item => {
        if (typeof item !== 'object' || item === null) return false;
        const virtual = item.virtual;
        return virtual === 0 || virtual === '0' || virtual === undefined || virtual === null;
      });
    },
    getVirtualList(row) {
      if (!row || !row.list || !Array.isArray(row.list)) return [];
      return row.list.filter(item => {
        if (typeof item !== 'object' || item === null) return false;
        return item.virtual === 1 || item.virtual === '1';
      });
    },
    getPlainItems(list) {
      if (!Array.isArray(list)) return [];
      return list.filter(item => typeof item !== 'object' || item === null);
    },
    getContractMaterialList(row) {
      if (!row) return [];
      const detailList = this.getContractDetailList(row);
      if (!detailList || detailList.length === 0) {
        return this.getMaterialList(row);
      }
      const allMaterials = [];
      detailList.forEach(detail => {
        const materials = this.getDetailMaterialList(detail);
        allMaterials.push(...materials);
      });
      return allMaterials;
    },
    getContractVirtualList(row) {
      if (!row) return [];
      const detailList = this.getContractDetailList(row);
      if (!detailList || detailList.length === 0) {
        return this.getVirtualList(row);
      }
      const allVirtuals = [];
      detailList.forEach(detail => {
        const virtuals = this.getDetailVirtualList(detail);
        allVirtuals.push(...virtuals);
      });
      return allVirtuals;
    },
    getContractPlainItems(row) {
      if (!row) return [];
      const detailList = this.getContractDetailList(row);
      if (!detailList || detailList.length === 0) {
        return this.getPlainItems(row.list || []);
      }
      const allPlainItems = [];
      detailList.forEach(detail => {
        const plainItems = this.getDetailPlainItems(detail);
        allPlainItems.push(...plainItems);
      });
      return allPlainItems;
    },
    formatDetailField(value) {
      if (value === undefined || value === null || value === '') {
        return '--';
      }
      return value;
    },
    formatDetailWeight(value) {
      if (value === undefined || value === null || value === '') {
        return '--';
      }
      const num = parseFloat(value);
      if (isNaN(num)) {
        return value;
      }
      return `${num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KG`;
    },
    refreshData() {
      if (this.selectedTenderIds.length > 0) {
        return this.fetchData2();
      }
      return this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
          status: 3,
          source: 1,
          keyword: this.searchKeyword || '',
          tenderEntityList: this.selectedTenderList.length > 0 ? this.selectedTenderList : undefined
        };

        const response = await request('/BidBrand/findbypage', 'post', params);

        if (response && response.data) {
          const data = response.data;
          if (data.records) {
            this.tableData = data.records;
            this.total = data.total || 0;
          } else if (data.list) {
            this.tableData = data.list;
            this.total = data.total || 0;
          } else if (Array.isArray(data)) {
            this.tableData = data;
            this.total = data.length;
          } else if (data.data) {
            if (Array.isArray(data.data)) {
              this.tableData = data.data;
              this.total = data.total || data.data.length;
            } else if (data.data.records) {
              this.tableData = data.data.records;
              this.total = data.data.total || 0;
            } else if (data.data.list) {
              this.tableData = data.data.list;
              this.total = data.data.total || 0;
            }
          } else {
            this.tableData = [];
            this.total = 0;
          }
        } else {
          this.tableData = [];
          this.total = 0;
        }
      } catch (error) {
        console.error('获取数据失败:', error);
        ElMessage.error('获取数据失败: ' + (error.message || '未知错误'));
        this.tableData = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    async fetchData2() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
          tenderEntityList: this.selectedTenderList
        };

        const response = await request('/productStore/reportbidBrandListPage', 'post', params);

        if (response && response.data) {
          const data = response.data;
          if (data.records) {
            this.tableData = data.records;
            this.total = data.total || 0;
          } else if (data.list) {
            this.tableData = data.list;
            this.total = data.total || 0;
          } else if (Array.isArray(data)) {
            this.tableData = data;
            this.total = data.length;
          } else if (data.data) {
            if (Array.isArray(data.data)) {
              this.tableData = data.data;
              this.total = data.total || data.data.length;
            } else if (data.data.records) {
              this.tableData = data.data.records;
              this.total = data.data.total || 0;
            } else if (data.data.list) {
              this.tableData = data.data.list;
              this.total = data.data.total || 0;
            }
          } else {
            this.tableData = [];
            this.total = 0;
          }
        } else {
          this.tableData = [];
          this.total = 0;
        }
      } catch (error) {
        console.error('获取数据失败:', error);
        ElMessage.error('获取数据失败: ' + (error.message || '未知错误'));
        this.tableData = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.page = 1;
      this.refreshData();
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.page = 1;
    },
    handleCurrentChange(val) {
      this.page = val;
    },
    getItemImageList(item) {
      if (!item) return [];
      if (Array.isArray(item.imgListbyidlist)) {
        return item.imgListbyidlist;
      }
      if (Array.isArray(item.imglist)) {
        return item.imglist;
      }
      if (item.image) {
        if (Array.isArray(item.image)) {
          return item.image;
        }
        return [item.image];
      }
      return [];
    },
    showImageList(payload) {
      let imgList = [];
      if (Array.isArray(payload)) {
        imgList = payload;
      } else if (payload && Array.isArray(payload.imgListbyidlist)) {
        imgList = payload.imgListbyidlist;
      } else if (payload && Array.isArray(payload.imglist)) {
        imgList = payload.imglist;
      }

      if (imgList && imgList.length > 0) {
        this.currentImageList = imgList.map(img => {
          return img.image || img;
        });
        this.imageDialogVisible = true;
      } else {
        ElMessage.warning('该记录暂无图片');
      }
    },
    handleCloseImageDialog() {
      this.imageDialogVisible = false;
      this.currentImageList = [];
    },
    formatFieldValue(key, value) {
      if (key === 'moneyALL' && value !== null && value !== undefined) {
        const num = parseFloat(value);
        if (!isNaN(num)) {
          return '¥' + num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
      }
      if (key === 'heaveyAll' && value !== null && value !== undefined) {
        const num = parseFloat(value);
        if (!isNaN(num)) {
          return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
      }
      return value;
    }
  }
};
</script>

<style lang="less" scoped>
@primary-color: #409eff;
@success-color: #67c23a;
@info-color: #909399;
@text-primary: #303133;
@text-regular: #606266;
@text-secondary: #909399;
@border-base: #dcdfe6;
@border-light: #e4e7ed;
@border-lighter: #ebeef5;
@bg-color: #ffffff;
@bg-page: #f5f7fa;
@shadow-base: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
@shadow-light: 0 2px 8px 0 rgba(0, 0, 0, 0.08);

.tender-entity-h5 {
  padding: 12px;
  background: @bg-page;
  min-height: 100vh;
  padding-bottom: 80px;
}

// 搜索框样式
.search-box-h5 {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  padding: 12px;
  background: @bg-color;
  border-radius: 8px;
  box-shadow: @shadow-light;

  .search-input-h5 {
    flex: 1;
    
    :deep(.el-input__wrapper) {
      border-radius: 8px;
    }
  }

  .search-btn-h5 {
    border-radius: 8px;
    padding: 0 20px;
    white-space: nowrap;
  }
}

// 网格视图容器
.grid-view-container-h5 {
  margin-top: 0;
}

.grid-list-h5 {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// 网格卡片样式
.grid-item-h5 {
  background: @bg-color;
  border: 1px solid @border-lighter;
  border-radius: 12px;
  padding: 16px;
  box-shadow: @shadow-light;
  transition: all 0.3s ease;

  &:active {
    transform: scale(0.98);
    box-shadow: @shadow-base;
  }
}

.grid-header-h5 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid @border-lighter;
  gap: 8px;
}

.grid-title-h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: @text-primary;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.display-tag {
  flex-shrink: 0;
}

.grid-body-h5 {
  margin-bottom: 12px;
}

.grid-info-h5 {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
  color: @text-regular;
  line-height: 1.5;
}

.grid-icon-h5 {
  margin-right: 8px;
  color: @primary-color;
  font-size: 16px;
  flex-shrink: 0;
}

.grid-footer-h5 {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid @border-lighter;
}

.image-btn-h5-full {
  width: 100%;
  border-radius: 8px;
}

.image-btn-h5 {
  width: 100%;
  border-radius: 6px;
  margin-top: 8px;
}

// 材料展开区域
.grid-material-section-h5,
.grid-detail-section-h5 {
  margin-top: 12px;
  margin-bottom: 12px;

  :deep(.el-collapse) {
    border: none;
  }

  :deep(.el-collapse-item__header) {
    font-size: 14px;
    font-weight: 500;
    padding: 8px 0;
    border-bottom: 1px solid @border-lighter;
  }

  :deep(.el-collapse-item__content) {
    padding: 12px 0;
  }
}

// 材料表格样式
.grid-material-table-wrapper-h5 {
  width: 100%;
  margin-top: 8px;
  overflow-x: auto;
  
  :deep(.el-table) {
    border-radius: 6px;
    overflow: hidden;
    font-size: 12px;
    
    .el-table__header-wrapper {
      .el-table__header {
        th {
          background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
          color: #606266;
          font-weight: 600;
          font-size: 12px;
          padding: 8px 4px;
          border-bottom: 2px solid #e4e7ed;
        }
      }
    }
    
    .el-table__body-wrapper {
      .el-table__body {
        tr {
          transition: all 0.3s ease;
          
          &:hover > td {
            background: linear-gradient(90deg, rgba(64, 158, 255, 0.05) 0%, rgba(64, 158, 255, 0.02) 100%) !important;
          }
          
          td {
            border-color: #ebeef5;
            font-size: 12px;
            padding: 8px 4px;
          }
        }
      }
    }
    
    &.el-table--striped {
      .el-table__body {
        tr.el-table__row--striped {
          td {
            background-color: #fafafa;
          }
        }
      }
    }
    
    .el-button {
      font-size: 11px;
      padding: 4px 8px;
    }
  }
}

.grid-material-plain-items-h5 {
  margin-top: 8px;
  
  .grid-material-text-h5 {
    padding: 8px 12px;
    background: @bg-page;
    border-radius: 4px;
    font-size: 13px;
    color: @text-regular;
    text-align: center;
    margin-bottom: 4px;
  }
}

// 详情条例样式
.grid-detail-item-h5 {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid @border-lighter;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
}

.grid-detail-header-h5 {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.grid-detail-index-h5 {
  font-weight: 600;
  color: @primary-color;
  flex-shrink: 0;
}

.grid-detail-info-h5 {
  flex: 1;
}

.grid-detail-row-h5 {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  font-size: 13px;
  line-height: 1.5;
}

.grid-detail-label-h5 {
  color: @text-secondary;
  margin-right: 8px;
  min-width: 60px;
  flex-shrink: 0;
}

.grid-detail-value-h5 {
  color: @text-regular;
  flex: 1;
  word-break: break-all;
}

.grid-detail-materials-h5 {
  margin-top: 12px;
}

.grid-detail-material-group-h5 {
  margin-bottom: 12px;
}

.grid-detail-material-group-title-h5 {
  font-size: 13px;
  font-weight: 500;
  color: @text-primary;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid @border-lighter;
}

// 空状态
.empty-grid-h5 {
  padding: 60px 20px;
  text-align: center;
}

// 图片弹窗样式
.image-dialog-h5 {
  :deep(.el-dialog) {
    margin: 5vh auto;
    max-height: 90vh;
  }

  :deep(.el-dialog__body) {
    padding: 12px;
    max-height: calc(90vh - 100px);
    overflow-y: auto;
  }
}

.image-list-container-h5 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.image-item-h5 {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: @bg-page;
}

.image-preview-h5 {
  width: 100%;
  height: 100%;

  :deep(.el-image__inner) {
    object-fit: cover;
  }
}

.image-slot-h5 {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: @text-secondary;
  font-size: 14px;
}

.empty-image-h5 {
  padding: 40px 0;
  text-align: center;
}

// 分页样式
.pagination-box-h5 {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: @bg-color;
  padding: 12px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;

  :deep(.el-pagination) {
    justify-content: center;
  }

  :deep(.el-pagination__total) {
    font-size: 13px;
  }

  :deep(.el-pager li) {
    min-width: 32px;
    height: 32px;
    line-height: 32px;
    font-size: 13px;
  }

  :deep(.btn-prev),
  :deep(.btn-next) {
    min-width: 32px;
    height: 32px;
    line-height: 32px;
  }
}

// 响应式适配
@media (min-width: 768px) {
  .tender-entity-h5 {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .grid-list-h5 {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .image-list-container-h5 {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1024px) {
  .grid-list-h5 {
    grid-template-columns: repeat(3, 1fr);
  }

  .image-list-container-h5 {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
